// Test panel for label printers. Select a printer and print a real product label.
// Uses the first available product — no product selection needed.

import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Printer, CheckCircle2, XCircle } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import productionService from '../../../services/productionService';
import { getPrintServiceUrl } from '../../../services/operatorService';
import { getPrintErrorMessage } from '../../../utils/printErrors';

interface PrinterOption {
  key: string;
  nickname: string;
  ip: string;
}

const PrinterTestPanel: React.FC = () => {
  const { t } = useLanguage();
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  // Fetch registered printers with IPs
  const { data: printers = [] } = useQuery<PrinterOption[]>(
    'printer-test-options',
    async () => {
      const all = await productionService.getConfigSettings();
      const ipMap: Record<string, string> = {};
      all.filter(s => s.setting_key.endsWith('_ip') && s.setting_key.startsWith('printer_'))
        .forEach(s => { ipMap[s.setting_key.replace(/_ip$/, '')] = s.setting_value; });

      return all
        .filter(s =>
          s.setting_key.startsWith('printer_') &&
          s.setting_key !== 'printer_subnet' &&
          !s.setting_key.endsWith('_ip')
        )
        .filter(s => ipMap[s.setting_key])
        .map(s => ({
          key: s.setting_key,
          nickname: s.setting_key.replace(/^printer_/, ''),
          ip: ipMap[s.setting_key],
        }));
    },
    { refetchOnMount: 'always' }
  );

  // Fetch first product for the sample label
  const { data: sampleProduct } = useQuery(
    'sample-product-for-test',
    async () => {
      const products = await productionService.getProducts();
      return products[0] || null;
    },
    { staleTime: 10 * 60 * 1000 }
  );

  const handleTestPrint = async () => {
    const printer = printers.find(p => p.key === selectedPrinter);
    if (!printer) return;

    setSending(true);
    setResult(null);
    try {
      const now = new Date();
      const label_data = {
        product_name: sampleProduct?.item_english_name || 'Test Product',
        product_chinese: sampleProduct?.item_chinese_name || '',
        product_sku: sampleProduct?.item_number || 'TEST-001',
        product_ingredient: '',
        lot_number: 'TEST-PRINT',
        box_number: 1,
        barcode_number: 'TEST-PRINT-001',
        actual_weight: sampleProduct?.target_weight || '5.00',
        target_weight: sampleProduct?.target_weight || '5.00',
        weight_unit: 'lbs',
        packed_date: now.toLocaleDateString(),
        packed_time: now.toLocaleTimeString(),
        packed_timestamp: now.toISOString(),
        operator_id: 'TEST',
        operator_name: 'Test Print',
        station_number: '1',
        station_name: 'Test',
        raw_material_lot: '',
        raw_material_type: '',
        production_location: 'M',
        qr_data: 'TEST-PRINT',
      };

      const base = getPrintServiceUrl();
      const url = base ? `${base}/api/print/html-label` : '/api/print/html-label';
      const token = localStorage.getItem('authToken');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ label_data, barcode_data: 'TEST-PRINT-001', copies: 1, printer_ip: printer.ip }),
      });
      const data = await res.json();
      setResult({
        success: data.success,
        message: data.success
          ? t('stations.testPrintSuccess')
          : getPrintErrorMessage(t, data.error_code, data.error, t('stations.testPrintFailed')),
      });
    } catch {
      setResult({ success: false, message: t('stations.testPrintFailed') });
    }
    setSending(false);
  };

  return (
    <div className="border-t pt-6 mt-6">
      <div className="flex items-center gap-2 mb-2">
        <Printer className="w-5 h-5 text-secondary-500" />
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white">{t('admin.printerTestPanel')}</h3>
      </div>
      <p className="text-xs text-secondary-500 dark:text-gray-400 mb-4">{t('admin.printerTestPanelDesc')}</p>

      <div className="flex items-center gap-4 flex-wrap">
        <select
          value={selectedPrinter}
          onChange={(e) => { setSelectedPrinter(e.target.value); setResult(null); }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">{t('admin.selectPrinter')}</option>
          {printers.map((p) => (
            <option key={p.key} value={p.key}>{p.nickname}</option>
          ))}
        </select>

        <button
          onClick={handleTestPrint}
          disabled={!selectedPrinter || sending}
          className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {sending ? t('common.loading') : t('admin.printTestLabel')}
        </button>

        {result && (
          <div className={`flex items-center gap-1.5 text-sm ${result.success ? 'text-green-600' : 'text-red-500'}`}>
            {result.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span className="whitespace-pre-line">{result.message}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrinterTestPanel;
